import type { IntlShape } from "react-intl";
import { Path, Shape } from "three";
import { type RefinementCtx, ZodIssueCode } from "zod";
import {
	type ThreeInputLinesType,
	ThreeShapeSideTypes,
	type ThreeSidePathsType,
} from "../../../../../../types/three";
import type { DimensionProperties } from "../../../../../../types/workpiece";
import type InputType from "../../../../Input/InputType";
import { JsonInputType } from "../../../../Input/JsonInputType";
import { SideName } from "../../../../Side/SideName";
import Angled from "../Angled";

export default class Width extends Angled {
	public static code = "plate_rectangle_angled_width";

	public getDimensionsString(input: InputType, intl: IntlShape) {
		if (input.length_1 && input.length_2 && input.width) {
			const { length_1, length_2, width } = this.formatInputs(input, intl, "decimal");

			return `(${length_1} x ${length_2}) x ${width} cm`;
		}
		return undefined;
	}

	public calculateSideLength(side: SideName, input: InputType) {
		const length_1 = Number(input[JsonInputType.LengthSection1]);
		const length_2 = Number(input[JsonInputType.LengthSection2]);
		const width = Number(input[JsonInputType.Width]);

		if (!(Number.isNaN(length_1) || Number.isNaN(length_2) || Number.isNaN(width))) {
			switch (side) {
				case SideName.Width1:
					return width;
				case SideName.Width2:
					return Math.sqrt(Math.abs(length_1 - length_2) ** 2 + width ** 2);
				case SideName.Length1:
					return length_1;
				case SideName.Length2:
					return length_2;
			}
		}

		return undefined;
	}

	public calculateSheetDimensions(input: InputType): { length: number; width: number } | undefined {
		const length_1 = Number(input[JsonInputType.LengthSection1]);
		const length_2 = Number(input[JsonInputType.LengthSection2]);
		const width = Number(input[JsonInputType.Width]);

		if (!(Number.isNaN(length_1) || Number.isNaN(length_2) || Number.isNaN(width))) {
			const largestLength = length_1 > length_2 ? length_1 : length_2;

			return { length: largestLength, width };
		}
	}

	protected validateBeltSize(data: Record<JsonInputType, number>, context: RefinementCtx): void {
		data.length = Math.max(Number(data.length_1), Number(data.length_2));

		const { biggest, smallest, maxSize } = this.calculateMaxBeltSize(data);

		if (smallest > maxSize) {
			context.addIssue({
				code: ZodIssueCode.custom,
				params: {
					type: "belt_size",
					field_1: [this.getInputById(JsonInputType.Width)?.label],
					field_2: [
						this.getInputById(JsonInputType.LengthSection1)?.label,
						this.getInputById(JsonInputType.LengthSection2)?.label,
					],
					maxValue: maxSize,
					value: biggest,
				},
				path: [JsonInputType.Width],
			});

			context.addIssue({
				code: ZodIssueCode.custom,
				params: {
					type: "belt_size",
					field_1: [
						this.getInputById(JsonInputType.LengthSection1)?.label,
						this.getInputById(JsonInputType.LengthSection2)?.label,
					],
					field_2: [this.getInputById(JsonInputType.Width)?.label],
					maxValue: maxSize,
					value: biggest,
				},
				path: [JsonInputType.LengthSection1],
			});

			context.addIssue({
				code: ZodIssueCode.custom,
				params: {
					type: "belt_size",
					field_1: [
						this.getInputById(JsonInputType.LengthSection1)?.label,
						this.getInputById(JsonInputType.LengthSection2)?.label,
					],
					field_2: [this.getInputById(JsonInputType.Width)?.label],
					maxValue: maxSize,
					value: biggest,
				},
				path: [JsonInputType.LengthSection2],
			});
		}
	}
	public getThreeShape(props: DimensionProperties["plate_rectangle_angled_width"]) {
		const [length_1, length_2, width] = [
			props.length_1 / 1000,
			props.length_2 / 1000,
			props.width / 1000,
		];

		const s = new Shape();

		s.moveTo(0, 0);
		s.lineTo(0, width);
		s.lineTo(length_1, width);
		s.lineTo(length_2, 0);
		s.lineTo(0, 0);

		return s;
	}

	public getThreeSidePaths(
		props: DimensionProperties["plate_rectangle_angled_width"],
	): ThreeSidePathsType[] {
		const [length_1, length_2, width] = [
			props.length_1 / 1000,
			props.length_2 / 1000,
			props.width / 1000,
		];

		return [
			{
				path: new Path().moveTo(0, 0).lineTo(length_2, 0),
				code: "length2",
				type: ThreeShapeSideTypes.side,
			},
			{
				path: new Path().moveTo(length_2, 0).lineTo(length_1, width),
				code: "width2",
				type: ThreeShapeSideTypes.side,
			},
			{
				path: new Path().moveTo(length_1, width).lineTo(0, width),
				code: "length1",
				type: ThreeShapeSideTypes.side,
			},
			{
				path: new Path().moveTo(0, width).lineTo(0, 0),
				code: "width1",
				type: ThreeShapeSideTypes.side,
			},
		];
	}

	public getThreeInputLines(
		props: DimensionProperties["plate_rectangle_angled_width"],
	): ThreeInputLinesType[] {
		const [length_1, length_2, width] = [
			props.length_1 / 1000,
			props.length_2 / 1000,
			props.width / 1000,
		];

		return [
			{
				dimension: props.length_1,
				path: new Path().moveTo(0, width).lineTo(length_1, width),
				type: ThreeShapeSideTypes.side,
				code: "length_1",
			},
			{
				dimension: props.length_2,
				path: new Path().moveTo(0, 0).lineTo(length_2, 0),
				type: ThreeShapeSideTypes.side,
				code: "length_2",
			},
			{
				dimension: props.width,
				path: new Path().moveTo(0, 0).lineTo(0, width),
				type: ThreeShapeSideTypes.side,
				code: "width",
			},
		];
	}

	public getDimensions(props: DimensionProperties["plate_rectangle_angled_width"]) {
		return {
			width: props.width ? props.width : 1000,
			length_1: props.length_1 ? props.length_1 : 2000,
			length_2: props.length_2 ? props.length_2 : 1500,
		};
	}
}
